import { useState } from "react";
import {
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useEmailConversation } from "../../email/api/getEmailConversation";
import SendEmail from "../../email/components/SendEmail";
import { useGetUserSocialProfile } from "../api/getUserSocialProfile";
import { createStyles, makeStyles } from "@mui/styles";
import { baseUrl } from "../../../shared/config";
import RichText from "../../../ui/components/RichText";
import _cookies from "../../../shared/utils/_cookies";
import clsx from "clsx";
import { useQueryClient } from "@tanstack/react-query";

const useStyles = makeStyles((theme) => ({
  "DirectMessages-Wrapper": {
    display: "flex",
    flexDirection: "column",
    height: "fit-content",
    [theme.breakpoints.up("md")]: {
      overflow: "hidden",
    },
    padding: 0,
  },
  "DirectMessages-Dialog": {},
  "DirectMessages-Chat": {
    [theme.breakpoints.down("md")]: {
      height: "90dvh",
    },
    [theme.breakpoints.up("md")]: {
      height: "40dvh",
    },
    overflow: "auto",
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  },
  "DirectMessages-Footer": {
    height: "100%",
    "& > form": {
      display: "flex",
    },
  },
  "DirectMessages-Message-Root": {
    width: "min(75%,300px)",
    borderRadius: "10px",
    padding: theme.spacing(1),
    margin: theme.spacing(2),
    "& > div > .quill > .ql-container > .ql-editor > p": {
      marginBottom: 0,
    },
  },
  "DirectMessages-Message-Sent": {
    marginLeft: "auto",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
  },
  "DirectMessages-Message-Recieved": {
    marginRight: "auto",
    color: theme.palette.getContrastText(theme.palette.primary.light),
    backgroundColor: theme.palette.primary.light,
  },
}));

export default function DirectMessages({ studentId }) {
  const conversationQuery = useEmailConversation({
    studentId,
    config: {
      staleTime: 30 * 1000,
    },
  });
  const recipientProfileQuery = useGetUserSocialProfile({ studentId });
  const queryClient = useQueryClient();
  const classes = useStyles();
  const theme = useTheme();
  const invalidateEmailQuery = () => {
    queryClient.invalidateQueries({ queryKey: ["emailConversation"] });
  };

  return (
    <DialogContent className={classes["DirectMessages-Wrapper"]} disa>
      <div className={classes["DirectMessages-Chat"]}>
        {conversationQuery.data?.map((message) => {
          const isRecievedMessage =
            message.alunoidDestinatario ==
            _cookies.getItem("auth").user.alunoid;
          return (
            <div
              className={clsx({
                [classes["DirectMessages-Message-Root"]]: true,
                [classes["DirectMessages-Message-Recieved"]]: isRecievedMessage,
                [classes["DirectMessages-Message-Sent"]]: !isRecievedMessage,
              })}
            >
              <RichText value={message.texto} />
            </div>
          );
        })}
      </div>
      <div className={classes["DirectMessages-Footer"]}>
        <SendEmail
          recipient={{
            studentId,
            name: recipientProfileQuery.data?.nomeAluno,
          }}
          defaultValues={{
            subject: "Mensagem trocada na Rede Social",
            message: "",
          }}
          canChangeSubject={false}
          onSuccess={invalidateEmailQuery}
        />
      </div>
    </DialogContent>
  );
}

export function DirectMessagesDialog({ children, ...rest }) {
  const { studentId } = rest;
  const [dialogOpen, setDialogOpen] = useState(false);
  const recipientProfileQuery = useGetUserSocialProfile({ studentId });

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  const methods = {
    openDialog: handleOpenDialog,
    closeDialog: handleCloseDialog,
  };
  const theme = useTheme();
  const isDisplaySmall = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <>
      {children(methods)}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth={"md"}
        fullScreen={isDisplaySmall}
        fullWidth
      >
        <IconButton
          sx={{ position: "absolute", top: 3, right: 3 }}
          onClick={handleCloseDialog}
        >
          <Close />
        </IconButton>{" "}
        <DialogTitle
          sx={{
            height: "75px",
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: `1px solid ${theme.palette.grey["400"]}`,
          }}
        >
          <Avatar
            src={`${baseUrl}/membros/fotos/${recipientProfileQuery.data?.foto}`}
          />
          {recipientProfileQuery.data?.nomeAluno}
        </DialogTitle>
        <DirectMessages {...rest} />
      </Dialog>
    </>
  );
}
